import { MutableRefObject, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Revenue } from "@arelia-types/earnings";
import { useEditableTableAPIFactory } from "./EditableTableAPI";
import { Typography, Box, Button, IconButton, CircularProgress } from "@mui/material";
import DeleteConfirmationDialog from "@arelia-components/ui-atoms/DeleteConfirmationDialog";
import { ColumnDef, DataGrid, editCells } from "@arelia-components/ui-atoms/data-grid";
import { ArelTooltip } from "@arelia-components/ui-atoms/Tooltip";
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';

// symbol to mark that row is changed
const rowChangedSymbol = Symbol('row changed');

export interface EditableTableContainerApi {
  addRow: () => void;
  save: () => void;
}

export interface EditableTableProps {
  columnConfig: ColumnDef[];
  apiRoute: string;

  onTotalsRefresh: () => void;
  getApiResponseMapper?: (data: Revenue[]) => any[];
  postApiResponseMapper?: (rowData: any[]) => Revenue[];
  onAddRow: () => void | undefined;
  apiParamKey?: string;
  apiRef?: MutableRefObject<EditableTableContainerApi | undefined>;
}

export default function EditableTableContainer({
  columnConfig,
  apiRoute,
  onTotalsRefresh,
  getApiResponseMapper,
  postApiResponseMapper,
  onAddRow,
  apiParamKey,
  apiRef
}: EditableTableProps) {
  const [rowToDelete, setRowToDelete] = useState<any | null>(null);
  const [allDataReceived, setAllDataReceived] = useState(false);
  const [gridData, setGridData] = useState<any[]>([]);
  const [gridState, setGridState] = useState<any>({});

  const {
    revenueAPIResponse,
    updateDataMutation,
    deleteDataMutation,
    getAllData
  } = useEditableTableAPIFactory(apiRoute, onTotalsRefresh, apiParamKey as string);

  useEffect(() => {
    let originalData = revenueAPIResponse?.data ?? [];

    if (getApiResponseMapper)
      originalData = getApiResponseMapper?.(originalData);

    setGridData(originalData.map(x => ({ ...x })));
  }, [revenueAPIResponse?.data]);

  useImperativeHandle(apiRef, () => ({
    addRow: () => {
      const newRow = { id: `new__${crypto.randomUUID()}` };
      setGridData([...gridData, newRow]);
      setGridState(editCells(gridState, newRow.id, columnDefs.map(x => x.field as string)));
    },
    save: () => {
      let editedData: any[] = gridData.filter(x => x[rowChangedSymbol]);

      if (postApiResponseMapper)
        editedData = postApiResponseMapper(editedData);

      if (editedData.length === 0)
        return;

      for (let row of editedData.filter(x => x.id.startsWith("new__")))
        delete row.id;

      updateDataMutation.mutate(editedData, {
        onSuccess: () => setGridState({})
      });
    }
  }));

  const receiveAllData = () => {
    getAllData();
    setAllDataReceived(true);
  };

  const handleDeleteRow = (confirmed: boolean) => {
    if (confirmed) {
      if (rowToDelete.id.startsWith("new__")) {
        setGridData(data => data.filter(x => x !== rowToDelete));
      } else {
        deleteDataMutation.mutate(rowToDelete.id);
      }
    }

    setRowToDelete(null);
  };

  const handleRowUpdate = ({ row }: any) => row[rowChangedSymbol] = true;

  const columnDefs = useMemo(() => [...columnConfig, {
    field: "actions",
    renderer: ({ row }: any) => (<ArelTooltip title={row.isReadOnly ? "Only manual records can be edited" : ""}>
      <Box>
        <IconButton aria-label="delete" onClick={() => setRowToDelete(row)} disabled={row.isReadOnly} color="error">
          <RemoveCircleOutlineOutlinedIcon />
        </IconButton>
      </Box>
    </ArelTooltip>)
  } as ColumnDef], [columnConfig]);

  if (gridData.length === 0)
    return <>
      <Typography variant="subtitle1" sx={{ textTransform: "uppercase", textAlign: "center", marginBottom: '30px', width: 750 }} >
        No data to display
      </Typography>
      <Button variant="text" startIcon={<AddOutlinedIcon />} onClick={() => onAddRow()} sx={{ width: "100%", marginTop: "4px" }}>
        Add
      </Button>
    </>;

  return (
    <>
      <DataGrid
        sx={{ marginTop: 2, marginBottom: 2, height: "100%", width: 750 }}
        columns={columnDefs}
        rows={gridData}
        getRowStyle={({ row }) => ({ opacity: row.isReadOnly ? 0.5 : 1 })}
        isRowEditable={({ row }) => !row.isReadOnly}
        onRowUpdate={handleRowUpdate}
        state={gridState}
        onStateChange={setGridState}
      />


      <Button variant="text" startIcon={<AddOutlinedIcon />} onClick={() => onAddRow()} sx={{ width: "100%", marginTop: "4px" }}>
        Add
      </Button>

      {(revenueAPIResponse?.totalRecordCount ?? 0) > 5 && !allDataReceived && (
        <Box justifyContent='center' display='flex' margin='45px 0 10px'>
          <Button variant="transparent" onClick={receiveAllData}>See All Events</Button>
        </Box>
      )}

      {rowToDelete ? <DeleteConfirmationDialog
        message={<>Are you sure you want to delete this item? <br />This action cannot be undone and will discard all changes.</>}
        onClose={handleDeleteRow}
      /> : <></>}

      {(updateDataMutation.isLoading || deleteDataMutation.isLoading) &&(
        <Box position="absolute" left="0" top="0" width="100%" height="100%" display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </Box>
      )}
    </>
  );
}